import { useMemo } from 'react'

import { useFetchExternalInvoiceFromBilly } from '@features/invoices/hooks/useFetchExternalInvoiceFromBilly'

import { ImportedInvoicePaymentFlags } from '../types/importedInvoicePaymentFlags'
import { ImportedInvoicePaymentStatus } from '../types/importedInvoicePaymentStatus'
import { getExternalInvoicePaymentStatus } from '../utils/getExternalInvoicePaymentStatus'

interface UseImportedInvoicePaymentFlagsParams {
  externalInvoiceId?: string
  paymentStatus?: ImportedInvoicePaymentStatus
}

/**
 * Billy books the payment at once, while rest-invoice's `paymentStatus` only follows a few seconds
 * later, when Billy's balance event lands. Billy is the source of truth, so while the two disagree
 * the flags follow Billy and report the action as pending.
 *
 * Right after a payment, the cached Billy invoice still has its old balance until the refetch
 * lands, so it agrees with rest-invoice and looks unpaid. The action stays pending until then,
 * so the payment cannot be booked a second time against the stale balance.
 */
export const useImportedInvoicePaymentFlags = ({
  externalInvoiceId,
  paymentStatus,
}: UseImportedInvoicePaymentFlagsParams): ImportedInvoicePaymentFlags => {
  const { externalInvoice, isFetching, isLoading } = useFetchExternalInvoiceFromBilly(externalInvoiceId ?? '')

  const externalInvoicePaymentStatus = externalInvoice ? getExternalInvoicePaymentStatus(externalInvoice) : undefined
  const isOutOfSync = Boolean(
    paymentStatus && externalInvoicePaymentStatus && externalInvoicePaymentStatus !== paymentStatus,
  )
  const isRefetching = Boolean(externalInvoice) && isFetching
  const isPending = isOutOfSync || isRefetching
  const canRegisterPayment = !isPending && externalInvoicePaymentStatus === ImportedInvoicePaymentStatus.Unpaid
  const shownPaymentStatus = isPending ? externalInvoicePaymentStatus : paymentStatus
  const isExternalInvoiceLoading = Boolean(externalInvoiceId) && isLoading

  return useMemo(
    () => ({
      canRegisterPayment,
      externalInvoice,
      isLoading: isExternalInvoiceLoading,
      isPending,
      paymentStatus: shownPaymentStatus,
    }),
    [canRegisterPayment, externalInvoice, isExternalInvoiceLoading, isPending, shownPaymentStatus],
  )
}
